import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import {
  FilePlus2, ArrowRight, Files, Users, FileText, TrendingUp, Loader2, UserCog, Sparkles, MessagesSquare,
  CheckCircle2, Circle, X, Compass, Briefcase, PartyPopper,
} from "lucide-react";

function Stat({ icon: Icon, label, value, sub, to, testid }) {
  const inner = (
    <Card data-testid={testid} className="rounded-2xl border-border p-6 h-full transition-transform hover:-translate-y-1">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4"><Icon className="h-5 w-5" /></div>
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="font-head text-2xl font-bold mt-1">{value}</div>
      {sub && <div className="text-sm text-muted-foreground mt-1 truncate">{sub}</div>}
    </Card>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [interview, setInterview] = useState(null);
  const [remindDismissed, setRemindDismissed] = useState(
    localStorage.getItem("hamn_interview_reminder") === new Date().toDateString()
  );
  const [onboardHidden, setOnboardHidden] = useState(
    localStorage.getItem("hamn_onboarding_dismissed") === "1"
  );
  const [doneHidden, setDoneHidden] = useState(
    localStorage.getItem("hamn_onboarding_done_dismissed") === "1"
  );
  const [guide, setGuide] = useState(null);
  const [available, setAvailable] = useState(false);
  const [availLoaded, setAvailLoaded] = useState(false);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
    api.get("/usage").then((r) => setUsage(r.data));
    api.get("/interview/progress").then((r) => setInterview(r.data)).catch(() => {});
    api.get("/availability").then((r) => setAvailable(r.data.available_for_jobs)).catch(() => {}).finally(() => setAvailLoaded(true));
    api.get("/guide-content").then((r) => setGuide(r.data.candidate || {})).catch(() => {});
  }, []);

  const toggleAvailable = async (val) => {
    setAvailable(val);
    try {
      await api.put("/availability", { available: val });
      toast.success(val ? "Du är nu öppen för jobb – synlig för arbetsgivare" : "Du är inte längre synlig för arbetsgivare");
    } catch { setAvailable(!val); toast.error("Kunde inte uppdatera"); }
  };

  const dismissRemind = () => {
    localStorage.setItem("hamn_interview_reminder", new Date().toDateString());
    setRemindDismissed(true);
  };
  const showRemind = interview && !remindDismissed && interview.answered > 0 &&
    (interview.percent < 100 || interview.summaries_count < interview.sections_total);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div>;

  const steps = data.onboarding || [];
  const doneCount = steps.filter((s) => s.done).length;
  const showOnboarding = steps.length > 0 && !data.onboarding_done && !onboardHidden;
  const dismissOnboarding = () => {
    localStorage.setItem("hamn_onboarding_dismissed", "1");
    setOnboardHidden(true);
  };
  const showDone = steps.length > 0 && data.onboarding_done && !doneHidden;
  const dismissDone = () => {
    localStorage.setItem("hamn_onboarding_done_dismissed", "1");
    setDoneHidden(true);
  };

  return (
    <div>
      <PageHeader
        title={`Hej ${data.name?.split(" ")[0] || ""}!`}
        description="Här är en överblick över din profil och dina CV:n. Ditt kandidat-ID är privat och pseudonymt."
      />

      <Card className={`rounded-2xl p-5 md:p-6 mb-6 flex items-center justify-between gap-4 transition-colors ${available ? "border-primary/40 bg-primary/[0.06]" : "border-border"}`} data-testid="availability-card">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`flex h-11 w-11 items-center justify-center rounded-xl shrink-0 ${available ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}><Briefcase className="h-5 w-5" /></div>
          <div className="min-w-0">
            <div className="font-head font-semibold flex items-center gap-2">Öppen för jobb {available && <span className="text-xs font-medium text-primary rounded-full bg-primary/10 px-2 py-0.5">Synlig</span>}</div>
            <p className="text-sm text-muted-foreground mt-0.5">
              {available
                ? "Arbetsgivare kan hitta din anonyma profil och kontakta dig – de ser bara ditt HAMN-id."
                : "Slå på för att bli synlig för arbetsgivares sökningar. Helt anonymt – aldrig namn, ålder eller kön."}
            </p>
          </div>
        </div>
        <Switch data-testid="dashboard-availability-switch" checked={available} disabled={!availLoaded} onCheckedChange={toggleAvailable} />
      </Card>

      {showOnboarding && (
        <Card className="rounded-2xl border-primary/25 bg-primary/[0.04] p-5 md:p-6 mb-6" data-testid="onboarding-card">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="flex items-center gap-2 text-primary text-sm font-medium mb-1"><Sparkles className="h-4 w-4" /> Kom igång</div>
              <h3 className="font-head font-semibold text-lg">Så bygger du din profil – {doneCount} av {steps.length} klart</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Följ stegen så skapar du snabbt ett ärligt, jobbanpassat CV. <Link to="/sa-funkar-det" className="text-primary font-medium hover:underline" data-testid="onboarding-guide-link">Läs guiden</Link>
              </p>
            </div>
            <button onClick={dismissOnboarding} className="shrink-0 flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-muted" aria-label="Dölj" data-testid="onboarding-dismiss-btn">
              <X className="h-5 w-5" />
            </button>
          </div>
          <Progress value={Math.round((doneCount / steps.length) * 100)} className="mb-4" />
          <div className="grid gap-2 sm:grid-cols-2">
            {steps.map((s) => (
              <Link
                key={s.id}
                to={s.to}
                data-testid={`onboarding-step-${s.id}`}
                className={`flex items-center gap-3 rounded-xl border px-3 py-3 min-h-[52px] transition-colors ${
                  s.done ? "border-primary/30 bg-white/60" : "border-border bg-white hover:border-primary/40"
                }`}
              >
                {s.done
                  ? <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                  : <Circle className="h-5 w-5 text-muted-foreground shrink-0" />}
                <span className={`text-sm flex-1 ${s.done ? "text-muted-foreground line-through" : "font-medium"}`}>{s.title}</span>
                {!s.done && <ArrowRight className="h-4 w-4 text-primary shrink-0" />}
              </Link>
            ))}
          </div>
        </Card>
      )}

      {showDone && (
        <Card className="rounded-2xl border-primary/30 bg-primary/[0.06] p-5 md:p-6 mb-6 relative overflow-hidden" data-testid="onboarding-done-card">
          <button onClick={dismissDone} className="absolute top-3 right-3 flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-muted" aria-label="Dölj" data-testid="onboarding-done-dismiss-btn">
            <X className="h-5 w-5" />
          </button>
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
              <PartyPopper className="h-6 w-6" />
            </div>
            <div className="min-w-0 pr-8">
              <h3 className="font-head font-bold text-xl" data-testid="onboarding-done-title">{guide?.done_title || "Grattis – din profil är redo!"}</h3>
              <p className="text-sm text-muted-foreground mt-1.5" data-testid="onboarding-done-text">{guide?.done_text || "Du har slutfört alla steg. Din masterprofil är komplett och du är redo att skapa skräddarsydda, ärliga CV:n för de jobb du söker. Lycka till!"}</p>
              <Link to="/app/skapa-cv" className="inline-block mt-4">
                <Button className="rounded-full" data-testid="onboarding-done-cta">
                  <FilePlus2 className="h-4 w-4 mr-2" /> {guide?.done_cta_label || "Skapa ett CV"}
                </Button>
              </Link>
            </div>
          </div>
        </Card>
      )}

      {showRemind && (
        <Card className="rounded-2xl border-primary/25 bg-primary/[0.04] p-4 md:p-5 mb-6 flex items-center gap-3 flex-wrap" data-testid="interview-reminder">
          <MessagesSquare className="h-5 w-5 text-primary shrink-0" />
          <p className="text-sm flex-1 min-w-[200px]">
            Du har påbörjat den <strong>guidade intervjun</strong> ({interview.percent}% klart). Vill du fortsätta där du slutade? Det tar bara några minuter.
          </p>
          <Link to="/app/intervju">
            <Button size="sm" className="rounded-full" data-testid="reminder-continue-btn">Fortsätt <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
          </Link>
          <Button size="sm" variant="ghost" className="rounded-full text-muted-foreground" onClick={dismissRemind} data-testid="reminder-dismiss-btn">Inte nu</Button>
        </Card>
      )}

      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4 mb-6">
        <Card className="rounded-2xl border-0 p-6 md:col-span-2 lg:col-span-2 text-primary-foreground" style={{ backgroundColor: "hsl(var(--brand-navy))" }}>
          <div className="flex items-center gap-2 text-primary-foreground/80 text-sm"><TrendingUp className="h-4 w-4" /> Din profil</div>
          <div className="flex items-end gap-2 mt-2">
            <span className="font-head text-5xl font-bold" data-testid="completeness-value">{data.completeness}%</span>
            <span className="mb-1.5 text-primary-foreground/80">komplett</span>
          </div>
          <Progress value={data.completeness} className="mt-4 bg-white/20" />
          <p className="text-sm text-primary-foreground/85 mt-4 flex items-center gap-2">
            <ArrowRight className="h-4 w-4" /> Nästa steg: {data.next_step}
          </p>
        </Card>

        <Stat testid="stat-ongoing" icon={Files} label="Pågående CV" value={data.ongoing_cv_count} to="/app/cv" />
        <Stat testid="stat-refs" icon={Users} label="Referenser" value={`${data.references_received} av ${data.references_total || 0}`} sub="inkomna" to="/app/referenser" />
      </div>

      <div className="grid gap-5 md:grid-cols-3 mb-8">
        <Stat testid="stat-latest" icon={FileText} label="Senaste CV" value={data.latest_cv ? data.latest_cv.label : "Inget ännu"} to={data.latest_cv ? `/app/cv/${data.latest_cv.id}` : "/app/skapa-cv"} />
        <Stat testid="stat-total" icon={Files} label="Totalt antal CV:n" value={data.total_cv_count} to="/app/cv" />
        {usage && (
          usage.unlimited ? (
            <Card data-testid="usage-card" className="rounded-2xl border-border p-6 bg-primary/5">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4"><Sparkles className="h-5 w-5" /></div>
              <div className="text-sm text-muted-foreground">Din plan</div>
              <div className="font-head text-2xl font-bold mt-1">Obegränsat</div>
              <div className="text-sm text-muted-foreground mt-1">Skapa så många CV:n du vill</div>
            </Card>
          ) : (
            <Card data-testid="usage-card" className="rounded-2xl border-border p-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4"><Sparkles className="h-5 w-5" /></div>
              <div className="text-sm text-muted-foreground">Gratis CV:n kvar</div>
              <div className="font-head text-2xl font-bold mt-1" data-testid="usage-remaining">{usage.remaining} av {usage.cv_quota}</div>
              <div className="text-sm text-muted-foreground mt-1">Därefter {usage.price_per_cv} kr per CV</div>
            </Card>
          )
        )}
      </div>

      {interview && (
        <Card className="rounded-2xl border-border p-6 mb-8" data-testid="interview-progress-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1"><MessagesSquare className="h-4 w-4 text-primary" /> Guidad intervju</div>
              <div className="flex items-end gap-2">
                <span className="font-head text-3xl font-bold" data-testid="interview-progress-percent">{interview.percent}%</span>
                <span className="mb-1 text-sm text-muted-foreground">besvarat · {interview.summaries_count} av {interview.sections_total} avsnitt sammanfattade</span>
              </div>
              <Progress value={interview.percent} className="mt-3 max-w-md" />
              <div className="flex flex-wrap gap-2 mt-3">
                {interview.sections.map((s) => (
                  <span key={s.id} className={`text-xs rounded-full px-2.5 py-1 border ${s.has_summary ? "border-primary/30 bg-primary/5 text-primary" : "border-border text-muted-foreground"}`}>
                    {s.title}: {s.answered}/{s.total}{s.has_summary ? " ✓" : ""}
                  </span>
                ))}
              </div>
            </div>
            <Link to="/app/intervju" className="shrink-0">
              <Button className="rounded-full" data-testid="dash-interview-btn">
                {interview.answered > 0 ? "Fortsätt intervjun" : "Starta intervjun"} <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-3">
        <Link to="/app/skapa-cv">
          <Button data-testid="dash-create-cv-btn" size="lg" className="w-full rounded-full h-14 text-base">
            <FilePlus2 className="h-5 w-5 mr-2" /> Skapa CV för ett jobb
          </Button>
        </Link>
        <Link to="/app/profil">
          <Button data-testid="dash-continue-profile-btn" size="lg" variant="outline" className="w-full rounded-full h-14 text-base">
            Fortsätt min profil
          </Button>
        </Link>
        <Link to="/app/cv">
          <Button data-testid="dash-my-cvs-btn" size="lg" variant="outline" className="w-full rounded-full h-14 text-base">
            Se mina CV:n
          </Button>
        </Link>
      </div>
    </div>
  );
}
